import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Icon } from '@/components/ui/icon.js';
import { fetchWalletBalances, WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { getOriginalsConfig } from '../api.js';
import { getDescriptor } from '../game-registry.js';
import { useOriginalsPlay } from '../useOriginalsPlay.js';
import { useIsImpersonating } from '@/features/auth/useIsImpersonating.js';
import { useGuestGate } from '../useGuestGate.js';
import { betLimitsOf } from '../state/bet.js';
import { BetPanel } from '../bet-panel/BetPanel.js';
import { GameStage } from './GameStage.js';
import { GameInfoBar } from './GameInfoBar.js';
import { BelowGame } from './BelowGame.js';
import { WheelLegend } from './WheelLegend.js';
import { CardsLegend } from './CardsLegend.js';
import { ScratchLegend } from './ScratchLegend.js';
import { DiceBoard } from '../selection/DiceBoard.js';
import { DicePayoutPreview } from '../selection/DicePayoutPreview.js';
import { KenoGrid } from '../selection/KenoGrid.js';
import { kenoMatrixFrom } from '../selection/KenoPayoutBar.js';
import { ProvablyFairPanel } from '../ProvablyFairPanel.js';
import { HowToPlay } from '../HowToPlay.js';
import { useGameSound } from '../effects/sound.js';
import { WinCelebration } from '../effects/WinCelebration.js';
import { MinesGameView } from '../mines/MinesGameView.js';
import { MinesPayoutPreview } from '../mines/MinesPayoutPreview.js';
import { resolveMinesMaxWinMinor } from '../mines/multiplier.js';
import { useMoney } from '@/lib/money/useMoney.js';
import { useDebouncedValue } from '@/lib/utils/useDebouncedValue.js';
import { MinesHowTo } from '../mines/MinesHowTo.js';
import { PlinkoGameView } from './PlinkoGameView.js';
import { hasStagePlate, originalTheme, originalThemeStyle } from '../theme.js';
import type { OriginalsConfig, ParamValue, GameSelection } from '../types.js';
import type { RoundResult } from '../bet-panel/auto/useAutoBet.js';
import type { SceneSelection } from '../scenes/types.js';

const BIG_WIN_MULTIPLIER = 10;
type Descriptor = NonNullable<ReturnType<typeof getDescriptor>>;

function toGameSelection(sel: SceneSelection | undefined): GameSelection | undefined {
  if (!sel) return undefined;
  if (sel.kind === 'dice') return { bet: sel.bet };
  return { picks: sel.picks };
}

// The /games/originals/$code route re-renders this same element when only $code
// changes, so React would keep all per-game state (chosen params, bet, selection,
// play phase) across game switches — a stale plinko `rows` sent to cards is exactly
// the server's ORIG-007 "unknown param". Keying by code remounts the whole subtree
// per game.
export function GamePage({ code }: { code: string }) {
  return (
    <div
      className="og-scope relative"
      style={originalThemeStyle(code)}
      data-og-motif={originalTheme(code)?.motif}
      data-og-plate={hasStagePlate(code) ? '' : undefined}
    >
      <div className="og-aura" aria-hidden />
      <GamePageInner key={code} code={code} />
    </div>
  );
}

function GamePageInner({ code }: { code: string }) {
  const { t } = useTranslation('originals');
  const navigate = useNavigate();
  const descriptor = getDescriptor(code);

  // chosen params; undefined → server applies defaults on first load
  const [params, setParams] = useState<Record<string, ParamValue> | undefined>(undefined);
  // Debounce the signature that drives the config refetch + scene rebuild. The bet panel still
  // reads the immediate `params`, so its controls stay responsive while dragging.
  // Settled so a range slider firing per step does not refetch the config and
  // remount the Pixi scene on every intermediate value.
  const debouncedParams = useDebouncedValue(params, 180);
  const configQuery = useQuery({
    queryKey: ['originals', 'config', code, debouncedParams],
    queryFn: () => getOriginalsConfig(code, debouncedParams),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev, // keep last config rendered while a param change refetches
  });
  const config = configQuery.data;

  // A param-change refetch keeps the last-good `config` via placeholderData, so a refetch error is
  // non-fatal: only hard-fail when there is NO prior config to fall back to (the initial load).
  if ((configQuery.isError && !config) || !descriptor) {
    return (
      <Card className="flex flex-col items-center gap-3 p-8 text-center">
        <span className="text-[14px] text-[var(--danger)]">{t('errorLoading')}</span>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/casino' })}>
          <Icon name="arrowRight" size={14} className="rotate-180" />
          {t('title')}
        </Button>
      </Card>
    );
  }
  if (configQuery.isPending || !config) {
    return <div className="h-[320px] rounded-2xl bg-[var(--bg-elevated)]/50 animate-pulse" />;
  }

  const protocol = descriptor.protocol ?? 'single-shot';
  if (protocol === 'stateful') {
    return (
      <MinesStatefulView config={config} />
    );
  }
  // Plinko runs its own concurrent-drop view (many balls in flight); every other single-shot
  // game keeps the shared single-flight SingleShotGameView. Scoping the concurrency here means
  // useOriginalsPlay and the other scenes are untouched.
  if (config.gameType === 'plinko') {
    return (
      <PlinkoGameView
        config={config}
        params={params}
        onParamsChange={setParams}
        configStale={configQuery.isError}
      />
    );
  }
  return (
    <SingleShotGameView
      code={code}
      config={config}
      descriptor={descriptor}
      params={params}
      onParamsChange={setParams}
      // refetch failed but we kept the last-good config — surface a non-blocking hint
      configStale={configQuery.isError}
    />
  );
}

// Stateful view for protocol='stateful' games (currently only Mines).
// Owns the bet + minesCount local state and delegates the grid to MinesGameView.
function MinesStatefulView({ config }: { config: OriginalsConfig }) {
  const limits = useMemo(() => betLimitsOf(config), [config]);
  // The agent's currency, from the same provider `MinesPayoutPreview` renders
  // money through — the config carries no currency of its own.
  const { currency } = useMoney();
  const [bet, setBet] = useState<bigint>(() => limits.min);
  const stageRef = useRef<HTMLDivElement>(null);

  // Resolve the mines count from resolvedParams, falling back to 3.
  const defaultMines =
    typeof config.resolvedParams?.mines === 'number' ? config.resolvedParams.mines : 3;
  const [minesCount, setMinesCount] = useState<number>(defaultMines);

  const sound = useGameSound();
  const [guideOpen, setGuideOpen] = useState(false);
  const [fairnessOpen, setFairnessOpen] = useState(false);

  // Latest mines round id (active or settled) so the fairness panel can verify it —
  // same wiring as the single-shot envelope roundId / plinko lastRoundId.
  const [roundId, setRoundId] = useState<string | undefined>(undefined);
  const handleRoundIdChange = useCallback((id: string | null) => setRoundId(id ?? undefined), []);

  // The server clamps every cashout at this, scaled to the agent's currency.
  // The ladder and the cashout label are drawn here, so they read the server's
  // number instead of keeping a THB copy of it.
  //
  // ABSENT is not zero. `maxWinMinor: 0n` is a real answer for wheel, keno and
  // scratch — no cap — but svc-game publishes a positive cap for mines
  // unconditionally, so an absent field here means this build of the SPA is
  // ahead of svc-game. Reading that as "no cap" made the whole preview
  // over-promise by up to 9.83x against what the server actually pays (W11).
  const maxWinMinor = useMemo(
    () => resolveMinesMaxWinMinor(config.maxWinMinor, currency),
    [config.maxWinMinor, currency],
  );

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1180px] mx-auto">
      <Card className="!p-0 overflow-hidden bg-[var(--bg-sunken)]">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-[340px] lg:shrink-0 order-2 lg:order-1 p-4 bg-[var(--og-rail)] border-t lg:border-t-0 lg:border-r border-transparent">
            <BetPanel
              config={config}
              limits={limits}
              bet={bet}
              onBet={setBet}
              params={{ ...config.resolvedParams, mines: minesCount } as Record<string, ParamValue>}
              onParam={(key, value) => {
                if (key === 'mines' && typeof value === 'number') setMinesCount(value);
              }}
              balance={null}
              busy={false}
              error={null}
              playRound={async () => ({ win: false, payout: 0n })}
              supportsAuto={false}
              selection={undefined}
              selectionValid={true}
              options={null}
              onAutoRunningChange={() => {}}
              hideCta
            />
            <MinesPayoutPreview bet={bet} minesCount={minesCount} maxWinMinor={maxWinMinor} />
            <MinesHowTo onMore={() => setGuideOpen(true)} />
          </div>
          <div ref={stageRef} className="og-stage flex-1 min-w-0 order-1 lg:order-2 flex flex-col">
            <MinesGameView
              bet={bet}
              minesCount={minesCount}
              onRoundIdChange={handleRoundIdChange}
              maxWinMinor={maxWinMinor}
            />
          </div>
        </div>
      </Card>
      <GameInfoBar
        code={config.gameCode}
        title={config.name}
        muted={sound.muted}
        onToggleSound={sound.toggleMute}
        onFairness={() => setFairnessOpen((v) => !v)}
        onHowTo={() => setGuideOpen(true)}
      />

      {fairnessOpen && <ProvablyFairPanel refreshKey={roundId} roundId={roundId} />}

      <BelowGame />

      <HowToPlay config={config} open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

interface ViewProps {
  code: string;
  config: OriginalsConfig;
  descriptor: Descriptor;
  params: Record<string, ParamValue> | undefined;
  onParamsChange: (p: Record<string, ParamValue>) => void;
  /** A param-change refetch failed; the rendered config is the last-good one. */
  configStale?: boolean;
}

function SingleShotGameView({ code, config, descriptor, params, onParamsChange, configStale = false }: ViewProps) {
  const { t } = useTranslation('originals');
  const navigate = useNavigate();
  const isImpersonating = useIsImpersonating();
  const guest = useGuestGate();
  const sound = useGameSound();
  const stageRef = useRef<HTMLDivElement>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [fairnessOpen, setFairnessOpen] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const limits = useMemo(() => betLimitsOf(config), [config]);
  const options = useMemo(
    () => ('options' in config.betLimits ? config.betLimits.options.map((o) => BigInt(o)) : null),
    [config],
  );
  const [bet, setBet] = useState<bigint>(() => limits.min); // config real at mount → init = min (= first option for scratch)

  const play = useOriginalsPlay(code);
  const resetPlay = play.reset;

  // active values = user choice ?? the defaults the server resolved
  const effectiveParams = (params ?? config.resolvedParams) as Record<string, ParamValue>;
  const setParam = useCallback(
    (key: string, value: ParamValue) => {
      // Drop the prior round's envelope so its win overlay can't persist (or replay on the scene
      // rebuild) onto the freshly-configured round.
      resetPlay();
      onParamsChange({ ...effectiveParams, [key]: value });
    },
    [effectiveParams, onParamsChange, resetPlay],
  );

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    staleTime: 15_000,
    enabled: !guest.isGuest,
  });

  const [selection, setSelection] = useState<SceneSelection | undefined>(undefined);
  // Editing the selection after a settled round starts the next one: drop the old
  // envelope so the board leaves its revealed state (KenoGrid stays interactive when
  // settled and relies on this reset to clear the drawn overlay + hit readout).
  const changeSelection = useCallback(
    (sel: SceneSelection) => {
      resetPlay();
      setSelection(sel);
    },
    [resetPlay],
  );
  const isSelectionValid = useCallback(
    (sel: SceneSelection | undefined): boolean =>
      !descriptor.selectable ? true
      : sel?.kind === 'dice' ? Boolean(sel.bet)
      : sel?.kind === 'keno' ? sel.picks.length >= 1
      : false,
    [descriptor.selectable],
  );
  const selectionValid = isSelectionValid(selection);

  // balanceAfter (envelope) is an exact minor-unit string. The wallet API still returns a
  // JS number (documented backend gap — wallet money column is mode:'number'); convert at that
  // boundary with a typeof guard so a future bigint/string return won't NaN. Never compute on it (§2.2).
  // Only adopt the envelope balance once the scene has revealed the result — the envelope lands while
  // phase is still 'playing', so using it earlier jumps the balance to the post-win amount before the
  // animation finishes and spoils the outcome.
  const balance: bigint | null =
    play.phase === 'revealed' && play.envelope?.balanceAfter != null
      ? BigInt(play.envelope.balanceAfter)
      : walletQuery.data
        ? typeof walletQuery.data.balance === 'number'
          ? BigInt(Math.round(walletQuery.data.balance))
          : BigInt(walletQuery.data.balance)
        : null;

  // shared playRound: fire play, settle after the scene reveal. On the error path the play hook
  // goes phase→idle with no envelope, so we REJECT the pending promise (else the auto loop's
  // `await playRound(bet)` would hang forever — B7 from review).
  const pending = useRef<{ resolve: (r: RoundResult) => void; reject: (e: unknown) => void } | null>(null);
  const playRound = useCallback(
    (b: bigint, sel?: SceneSelection): Promise<RoundResult> =>
      new Promise<RoundResult>((resolve, reject) => {
        const activeSel = sel ?? selection;
        // Hard guard: never call play() for a selectable game without a valid selection — this
        // closes the ORIG-008 path entirely (the disabled CTA already blocks the manual click,
        // but auto-bet drives playRound directly). Reject so the auto loop stops, not hangs.
        if (!isSelectionValid(activeSel)) {
          reject(new Error('ORIG-008'));
          return;
        }
        pending.current = { resolve, reject };
        sound.play('click');
        void play.play(Number(b), effectiveParams as Record<string, string | number>, toGameSelection(activeSel));
      }),
    [play, sound, effectiveParams, selection, isSelectionValid],
  );

  useEffect(() => {
    if (!pending.current) return;
    if (play.phase === 'revealed' && play.envelope) {
      const p = pending.current;
      pending.current = null;
      p.resolve({ win: play.envelope.win, payout: BigInt(play.envelope.payout) });
    } else if (play.phase === 'idle' && play.error) {
      const p = pending.current;
      pending.current = null;
      p.reject(new Error(play.error));
    }
  }, [play.phase, play.envelope, play.error]);

  const celebration =
    play.phase === 'revealed' && play.envelope?.win
      ? {
          payout: play.envelope.payout,
          multiplier: play.envelope.multiplier,
          big: Number(play.envelope.multiplier) >= BIG_WIN_MULTIPLIER,
        }
      : null;

  // Dice's betting board renders BELOW the bowl scene in normal flow (not an absolute overlay) so
  // it reads as a proper Hi-Lo table with room to breathe, and the bowl gets the full stage above.
  // Keno is NOT an overlay either: it owns the whole stage (single DOM grid that both selects AND
  // animates the sequential reveal) — rendered below.
  const isDice = config.gameType === 'dice';
  const isKeno = config.gameType === 'keno';
  const kenoMatrix = useMemo(
    () => (isKeno ? kenoMatrixFrom(config.presenterConfig) : null),
    [isKeno, config.presenterConfig],
  );

  // The payout legend renders in the stage column on desktop, but below the bet
  // panel on mobile (rendered twice, one shown per breakpoint) so the mobile core
  // loop — scene + amount + Bet — stays above the fold and the legend scrolls under it.
  const hasLegend =
    config.gameType === 'wheel' || config.gameType === 'cards' || config.gameType === 'scratch';
  const renderLegend = () =>
    config.gameType === 'wheel' ? <WheelLegend config={config} />
    : config.gameType === 'cards' ? (
        <CardsLegend
          config={config}
          envelope={play.envelope}
          phase={play.phase}
          onHowTo={() => setGuideOpen(true)}
        />
      )
    : config.gameType === 'scratch' ? <ScratchLegend config={config} bet={bet} />
    : null;

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1180px] mx-auto">
      {configStale && (
        <p className="text-[12px] text-[var(--warning,var(--danger))] text-center" role="status">
          {t('configStale')}
        </p>
      )}
      {/* Unified game card: bet rail + play area share one framed surface (bc.game style).
          Full-bleed on phones (cancel the page's px-4) so width-bound scenes — wheel/cards/
          scratch/keno/mines — get the whole screen width; framed again from md up. */}
      <Card className="!p-0 overflow-hidden bg-[var(--bg-sunken)] max-md:-mx-4 max-md:rounded-none">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-[340px] lg:shrink-0 order-2 lg:order-1 p-4 bg-[var(--og-rail)] border-t lg:border-t-0 lg:border-r border-transparent">
            <BetPanel
              config={config}
              limits={limits}
              bet={bet}
              onBet={setBet}
              params={effectiveParams}
              onParam={setParam}
              balance={balance}
              busy={play.phase === 'playing'}
              error={play.error}
              errorKind={play.errorKind}
              onRelogin={() => void navigate({ to: '/login' })}
              onGuestPlay={guest.isGuest ? guest.signIn : undefined}
              playRound={playRound}
              supportsAuto={descriptor.supportsAuto}
              selection={selection}
              selectionValid={selectionValid}
              options={options}
              onAutoRunningChange={setAutoRunning}
              hideCta={isImpersonating}
              selectionSlot={
                isDice ? (
                  <DiceBoard
                    selection={selection}
                    onChange={changeSelection}
                    disabled={play.phase === 'playing' || autoRunning}
                  />
                ) : undefined
              }
            />
            {isDice && <DicePayoutPreview bet={bet} selection={selection} />}
          </div>
          <div className="og-stage flex-1 min-w-0 order-1 lg:order-2 flex flex-col">
            {isKeno ? (
              <div ref={stageRef} className="relative w-full overflow-hidden">
                <KenoGrid
                  selection={selection}
                  onChange={changeSelection}
                  disabled={play.phase === 'playing' || autoRunning}
                  phase={play.phase}
                  envelope={play.envelope}
                  onRevealComplete={play.onRevealComplete}
                  turbo={autoRunning}
                  paytableMatrix={kenoMatrix}
                  bet={bet}
                />
                <WinCelebration
                  show={play.phase === 'revealed' && celebration != null}
                  amountMinor={celebration?.payout ?? '0'}
                  multiplier={celebration?.multiplier ?? '0'}
                  big={celebration?.big ?? false}
                  currency={play.envelope?.currency}
                />
              </div>
            ) : (
              <GameStage
                // Remount the Pixi scene when the RESOLVED config changes so it rebuilds from the new
                // presenterConfig (e.g. the wheel's slice count/ring) — usePixiScene captures onReady once
                // per mount and never re-runs it, so a key change is how the scene picks up a new segment
                // count (B5). Keying on config.resolvedParams (which tracks the debounced fetch) rather than
                // the immediate effectiveParams means a slider drag rebuilds the scene once on settle, not
                // on every intermediate step.
                key={`${code}:${JSON.stringify(config.resolvedParams)}`}
                ref={stageRef}
                Scene={descriptor.scene}
                config={config}
                envelope={play.envelope}
                phase={play.phase}
                turbo={autoRunning}
                onRevealComplete={play.onRevealComplete}
                celebration={celebration}
                selection={selection}
              />
            )}
            {/* Desktop only: the wide stage hosts the board. On mobile it renders
                under the CTA via BetPanel's selectionSlot so the bet button stays
                at the top of the controls. */}
            {isDice && (
              <div className="hidden lg:block px-3 pb-4 sm:px-4">
                <DiceBoard
                  selection={selection}
                  onChange={changeSelection}
                  disabled={play.phase === 'playing' || autoRunning}
                />
              </div>
            )}
            {hasLegend && (
              <div className="hidden lg:block px-4 pb-4">{renderLegend()}</div>
            )}
          </div>
        </div>
      </Card>

      {/* Mobile only: legend below the bet panel (desktop shows it in the stage column). */}
      {hasLegend && (
        <div className="og-stage lg:hidden rounded-2xl px-4 py-4">{renderLegend()}</div>
      )}

      <GameInfoBar
        code={code}
        title={config.name}
        muted={sound.muted}
        onToggleSound={sound.toggleMute}
        onFairness={() => setFairnessOpen((v) => !v)}
        onHowTo={() => setGuideOpen(true)}
      />

      {fairnessOpen && <ProvablyFairPanel refreshKey={play.envelope?.roundId} roundId={play.envelope?.roundId} />}

      <BelowGame />

      <HowToPlay config={config} open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
